"use client";

import { useCallback } from "react";
import { usePostHog } from "@posthog/next";
import { useTranslation } from "react-i18next";

/** Where on the site a demo CTA was clicked. */
export type DemoCtaPlacement =
  | "nav"
  | "nav_mobile"
  | "footer_contact"
  | "footer_demo"
  | "pricing_card"
  | "pricing_comparison"
  | "hero_email"
  | "cta_email"
  | "hero"
  | "section"
  | "bottom_cta";

export const DEMO_CTA_CLICKED_EVENT = "marketing_demo_cta_clicked";

/**
 * Returns a stable callback that records a click on any "book a demo" CTA so we
 * can see which button (and on which page) people use to start the demo flow.
 * The detailed `source` mirrors the value appended to the contact-sales URL.
 */
export function useTrackDemoCtaClick() {
  const posthog = usePostHog();
  const { i18n } = useTranslation();

  return useCallback(
    (args: {
      source: string;
      placement: DemoCtaPlacement;
      properties?: Record<string, unknown>;
    }) => {
      try {
        // Spread caller props first so the canonical fields below can't be overridden.
        posthog.capture(DEMO_CTA_CLICKED_EVENT, {
          ...args.properties,
          source: args.source,
          placement: args.placement,
          language: i18n.language,
        });
      } catch {
        // Analytics being unavailable must never break a navigation.
      }
    },
    [i18n.language, posthog],
  );
}
